import { FunctionComponent } from 'react';
import {
  OnboardingCostFactors,
  OnboardingCareSettingsPhaseOne,
  OnboardingCareSettingsPhaseTwo,
  OnboardingCareSettingsPhaseThree,
  OnboardingFamilyCaregivingPhaseOne,
  OnboardingFamilyCaregivingPhaseTwo,
  OnboardingFamilyCaregivingPhaseThree,
  OnboardingCarePhasesPhaseOne,
  OnboardingCarePhasesPhaseTwo,
  OnboardingCarePhasesPhaseThree,
  OnboardingCarePhasesSummary,
  OnboardingOurPredictions,
  OnboardingPredictedCareNeeds,
  OnboardingFundingCoverage,
  OnboardingFundingSources,
  OnboardingResultsAreIn,
  OnboardingDownloadReport,
  OnboardingPolicyApplicationDashboard,
  OnboardingOverview,
} from '@/pages';
import { StepDef, getStepDefs } from './ProgressStepper';

import _ from 'lodash';

export const onboardingSlideByIndex: FunctionComponent<ClientIdProps>[] = [
  OnboardingOverview,
  OnboardingResultsAreIn,
  OnboardingOurPredictions,
  OnboardingPredictedCareNeeds,
  OnboardingCostFactors,
  OnboardingCarePhasesPhaseOne,
  OnboardingCareSettingsPhaseOne,
  OnboardingFamilyCaregivingPhaseOne,
  OnboardingCarePhasesPhaseTwo,
  OnboardingCareSettingsPhaseTwo,
  OnboardingFamilyCaregivingPhaseTwo,
  OnboardingCarePhasesPhaseThree,
  OnboardingCareSettingsPhaseThree,
  OnboardingFamilyCaregivingPhaseThree,
  OnboardingCarePhasesSummary,
  OnboardingFundingSources,
  OnboardingPolicyApplicationDashboard,
  OnboardingFundingCoverage,
  OnboardingDownloadReport,
];

export const onboardingSlideFirst = onboardingSlideByIndex[0];
export const onboardingSlideLast = onboardingSlideByIndex[onboardingSlideByIndex.length - 1];

export function selectSlides(
  client: Client,
  featureFlags: FeatureFlags,
): FunctionComponent<ClientIdProps>[] {
  const {
    intakeSurvey: { customHiddenFields },
  } = client;

  if (featureFlags.vbShop && customHiddenFields.customSkipCarePlanning === 'true') {
    return excludeSlidesByStepName(client, 'Personalize Plan');
  }

  return onboardingSlideByIndex;
}

function excludeSlidesByStepName(
  client: Client,
  stepName: StepDef['name'],
): FunctionComponent<ClientIdProps>[] {
  const stepDefs = getStepDefs(client);
  const step = stepDefs.find(step => step.name === stepName)!;
  // If the step is 'Personalize Plan', we want to exclude all care phase slides regardless of when their first care phase is (for on-claim)
  const beginningSlideIndex =
    stepName === 'Personalize Plan'
      ? onboardingSlideByIndex.indexOf(OnboardingCarePhasesPhaseOne)
      : step.beginningSlideIndex;
  const lastSlideIndex = step.lastSlideIndex;

  return onboardingSlideByIndex.filter((slide, index) => {
    return index < beginningSlideIndex || index > lastSlideIndex;
  });
}

export function isSelectedSlidesFiltered(
  selectedSlides: FunctionComponent<ClientIdProps>[],
): boolean {
  return !_.isEqual(selectedSlides, onboardingSlideByIndex);
}

export const onboardingSlideVideoUrls = new Map<FunctionComponent<ClientIdProps>, string>([
  [OnboardingResultsAreIn, 'https://www.loom.com/embed/ba5e6a8ee0ea43af948f2b3f9acd3cc6'],
  [OnboardingOurPredictions, 'https://www.loom.com/embed/7658b90ecbb54cf887ae42fa3bab1a36'],
  [OnboardingPredictedCareNeeds, 'https://www.loom.com/embed/c7c2c346cf0644a2abed74e43c627368'],
  [OnboardingCostFactors, 'https://www.loom.com/embed/1d5609f345194b1ea83965e71966a94a'],
  [OnboardingCarePhasesPhaseOne, 'https://www.loom.com/embed/cb0db5c296e545388dc6652b17f4c7eb'],
  [OnboardingCareSettingsPhaseOne, 'https://www.loom.com/embed/3ae72ed2bbd34bd58085314810f3692f'],
  [
    OnboardingFamilyCaregivingPhaseOne,
    'https://www.loom.com/embed/105ffa8741ee41979ab1de5f03f4e181',
  ],
  [OnboardingCareSettingsPhaseTwo, 'https://www.loom.com/embed/5331f668f77a4d019f1e87ab7af343d1'],
  [
    OnboardingFamilyCaregivingPhaseTwo,
    'https://www.loom.com/embed/bd6031210d04453cabb52dd9236a1314',
  ],
  [OnboardingCareSettingsPhaseThree, 'https://www.loom.com/embed/d2e76c4356184cdfb048e2bebf3b0526'],
  [
    OnboardingFamilyCaregivingPhaseThree,
    'https://www.loom.com/embed/9beb759bcb3747f6a935bca2fbff3326',
  ],
  [OnboardingCarePhasesSummary, 'https://www.loom.com/embed/540ea4d8ee5148ffa7bc6cd600a4113a'],
]);
